/**
 * Minimal markdown rendering for help content (OPSFL-HELP-1). The contract format is PLAIN
 * markdown (no HTML/images), so this hand-rolled renderer covers exactly what content can carry:
 * paragraphs, `- ` bullets, `**bold**`, `` `code` `` — plus glossary-term hovers: plain-text runs
 * are scanned for the display terms of the ids a surface declares, wrapped in <Term>. No new deps.
 */
import { Fragment, type ReactNode } from 'react';
import { Term } from './Term';

export interface TermRef {
  id: string;
  display: string;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Wrap occurrences of the given glossary display terms (longest first, case-insensitive). */
function withTerms(text: string, terms: TermRef[] | undefined, keyBase: string): ReactNode {
  if (!terms || terms.length === 0 || !text) return text;
  const sorted = [...terms].sort((a, b) => b.display.length - a.display.length);
  const idByLower = new Map(sorted.map((t) => [t.display.toLowerCase(), t.id]));
  const pattern = new RegExp(`(${sorted.map((t) => escapeRegExp(t.display)).join('|')})`, 'gi');
  const parts = text.split(pattern);
  if (parts.length === 1) return text;
  return parts.map((part, i) => {
    const id = idByLower.get(part.toLowerCase());
    return id ? (
      <Term key={`${keyBase}-t${i}`} id={id}>
        {part}
      </Term>
    ) : (
      <Fragment key={`${keyBase}-p${i}`}>{part}</Fragment>
    );
  });
}

/** Inline markdown: `**bold**` and `` `code` ``; everything else is text (term-scanned). */
export function InlineMd({ text, terms }: { text: string; terms?: TermRef[] }) {
  const tokens = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean);
  return (
    <>
      {tokens.map((tok, i) => {
        if (tok.startsWith('**') && tok.endsWith('**') && tok.length > 4) {
          return <strong key={i}>{withTerms(tok.slice(2, -2), terms, `b${i}`)}</strong>;
        }
        if (tok.startsWith('`') && tok.endsWith('`') && tok.length > 2) {
          return (
            <code key={i} className="ol-mono" style={{ fontSize: '0.92em' }}>
              {tok.slice(1, -1)}
            </code>
          );
        }
        return <Fragment key={i}>{withTerms(tok, terms, `x${i}`)}</Fragment>;
      })}
    </>
  );
}

/** Block markdown: blank-line paragraphs + `- ` bullet lists (the contract allows nothing else). */
export function MdBlocks({ markdown, terms }: { markdown: string; terms?: TermRef[] }) {
  const lines = markdown.split('\n');
  const blocks: { kind: 'p' | 'ul'; items: string[] }[] = [];
  for (const line of lines) {
    const bullet = /^-\s+(.*)$/.exec(line);
    const last = blocks[blocks.length - 1];
    if (bullet) {
      if (last?.kind === 'ul') last.items.push(bullet[1]);
      else blocks.push({ kind: 'ul', items: [bullet[1]] });
    } else if (/^\s+\S/.test(line) && last?.kind === 'ul' && last.items.length > 0) {
      // Indented soft-wrap continuation of the previous bullet.
      last.items[last.items.length - 1] += ` ${line.trim()}`;
    } else if (!line.trim()) {
      blocks.push({ kind: 'p', items: [] }); // paragraph break
    } else if (last?.kind === 'p' && last.items.length > 0) {
      last.items[last.items.length - 1] += ` ${line.trim()}`;
    } else if (last?.kind === 'p') {
      last.items.push(line.trim());
    } else {
      blocks.push({ kind: 'p', items: [line.trim()] });
    }
  }
  return (
    <>
      {blocks.map((b, i) =>
        b.kind === 'ul' ? (
          <ul key={i} style={{ margin: '4px 0 8px', paddingInlineStart: 20 }}>
            {b.items.map((item, j) => (
              <li key={j} style={{ marginBottom: 4 }}>
                <InlineMd text={item} terms={terms} />
              </li>
            ))}
          </ul>
        ) : (
          b.items.length > 0 && (
            <p key={i} style={{ margin: '0 0 8px' }}>
              <InlineMd text={b.items.join(' ')} terms={terms} />
            </p>
          )
        ),
      )}
    </>
  );
}
